import React, { useEffect, useRef } from 'react';
import { ContentItem, OriginLocation } from 'shared/types/contentTypes';
import ContentFeedItem from '../ContentFeedCard/ContentFeedCard';
import GestureCard from '../GestureCard/GestureCard';

interface ContentFeedProps {
  items: ContentItem[];
  isLoading?: boolean;
  title?: string;
  className?: string;
  emptyMessage?: string;
  onItemClick?: (item: ContentItem) => void;
  // Infinite scroll props
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  // Current location context - used to determine if we should show origin location
  currentLocation?: OriginLocation;
}

const ContentFeedSkeleton: React.FC = () => (
  <div className="animate-pulse mb-4">
    <div className="bg-white border border-gray-100 rounded-xl p-4">
      <div className="flex gap-3">
        <div className="bg-gray-200 w-16 h-16 rounded-lg flex-shrink-0"></div>
        <div className="flex-grow">
          <div className="h-2 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
          <div className="h-2 bg-gray-200 rounded mb-1 w-full"></div>
          <div className="h-2 bg-gray-200 rounded mb-4 w-5/6"></div>
          <div className="flex justify-between pt-2">
            <div className="h-2 bg-gray-200 rounded w-1/4"></div>
            <div className="h-2 bg-gray-200 rounded w-1/4"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ContentFeed: React.FC<ContentFeedProps> = ({
  items = [],
  isLoading = false,
  title,
  className = '',
  emptyMessage = "No content items to display",
  onItemClick,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  currentLocation
}) => {
  const observerTarget = useRef<HTMLDivElement>(null);

  // Infinite scroll observer
  useEffect(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [onLoadMore, hasMore, isLoadingMore]);

  return (
    <div className={`${className}`}>
      {title && (
        <h2 className="font-syne text-xl sm:text-2xl font-bold text-gray-800 mb-4">{title}</h2>
      )}

      <div className="space-y-4">
        {isLoading ? (
          <>
            <ContentFeedSkeleton />
            <ContentFeedSkeleton />
            <ContentFeedSkeleton />
          </>
        ) : items.length > 0 ? (
          <>
            {/* Check if all items are gestures to apply consistent padding */}
            {items.every(item => item.contentType === 'gesture') ? (
              <div className="max-w-3xl mx-auto px-6">
                <div className="space-y-4">
                  {items.map((item) => (
                    <GestureCard
                      key={item.contentId}
                      item={item}
                      onClick={onItemClick ? () => onItemClick(item) : undefined}
                      currentLocation={currentLocation}
                    />
                  ))}
                </div>
              </div>
            ) : (
              items.map((item) => {
                // Use GestureCard for gesture items, ContentFeedItem for everything else
                if (item.contentType === 'gesture') {
                  return (
                    <GestureCard
                      key={item.contentId}
                      item={item}
                      onClick={onItemClick ? () => onItemClick(item) : undefined}
                      showTimestamp={false}
                      cardStyle="solid"
                      currentLocation={currentLocation}
                    />
                  );
                }

                return (
                  <ContentFeedItem
                    key={item.contentId}
                    item={item}
                    onClick={onItemClick ? () => onItemClick(item) : undefined}
                  />
                );
              })
            )}

            {/* Loading more indicator */}
            {isLoadingMore && (
              <>
                <ContentFeedSkeleton />
                <ContentFeedSkeleton />
              </>
            )}

            {/* Infinite scroll trigger */}
            {hasMore && !isLoadingMore && (
              <div ref={observerTarget} className="h-4" />
            )}
          </>
        ) : (
          <div className="bg-white border border-gray-100 rounded-xl p-8 text-center">
            <p className="text-gray-500">{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentFeed;
